import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, Download } from 'lucide-react';
import { DateRangePicker } from './DateRangePicker';
import { cn } from '@/lib/utils';

// Types (should be shared, but defining here for now or imported)
type DateRange = {
    start: Date | null;
    end: Date | null;
};

type FilterType = '1d' | '7d' | '30d' | 'custom';

interface UsageFiltersProps {
    dateRange: DateRange;
    activeFilter: FilterType;
    onFilterChange: (type: FilterType, range?: DateRange) => void;
}

export const UsageFilters = ({ dateRange, activeFilter, onFilterChange }: UsageFiltersProps) => {
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    // Format date range for display button
    const formatDateDisplay = () => {
        if (!dateRange.start || !dateRange.end) return 'Seleccionar fechas';

        const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
        const start = dateRange.start.toLocaleDateString('es-ES', options);
        const end = dateRange.end.toLocaleDateString('es-ES', options);

        // Capitalize months
        const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

        return `${capitalize(start)} - ${capitalize(end)}`;
    };

    const handleRangeChange = (newRange: DateRange) => {
        onFilterChange('custom', newRange);
    };

    return (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 relative">
            <div className="flex items-center gap-3">
                {/* Date Picker Trigger */}
                <div className="relative">
                    <Button
                        variant="outline"
                        onClick={() => setIsPickerOpen(!isPickerOpen)}
                        className={cn(
                            "h-9 gap-2 border-gray-200 text-gray-700 bg-white hover:bg-gray-50 font-normal transition-all",
                            activeFilter === 'custom' && "border-gray-900 ring-1 ring-gray-900"
                        )}
                    >
                        <span>{formatDateDisplay()}</span>
                        <ChevronDown size={14} className="text-gray-400" />
                    </Button>

                    <DateRangePicker
                        isOpen={isPickerOpen}
                        onClose={() => setIsPickerOpen(false)}
                        range={dateRange}
                        onChange={handleRangeChange}
                    />
                </div>

                {/* Quick Range Toggles */}
                <div className="bg-gray-100 p-0.5 rounded-lg flex items-center">
                    {(['1d', '7d', '30d'] as FilterType[]).map((filter) => (
                        <button
                            key={filter}
                            onClick={() => onFilterChange(filter)}
                            className={cn(
                                "px-3 py-1 text-sm font-medium rounded-md transition-all duration-200",
                                activeFilter === filter
                                    ? "bg-white text-gray-900 shadow-sm"
                                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                            )}
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>

            {/* Audit C-07: la exportación todavía no existe. La dejamos
                deshabilitada y rotulada para no prometer una acción muerta. */}
            <Button
                variant="outline"
                disabled
                title="Disponible próximamente"
                className="gap-2 h-9 border-gray-200 text-gray-400 bg-white cursor-not-allowed opacity-60"
            >
                <Download size={16} />
                <span className="text-sm">Exportar CSV (pronto)</span>
            </Button>
        </div>
    );
};
